/**
 * @fileoverview DSO suggestion list component with advanced keyboard navigation
 *
 * **Responsibilities:**
 * - Renders filtered product suggestions in a dropdown
 * - Manages keyboard navigation state (arrow keys, Home, End, Enter, Escape)
 * - Handles auto-scrolling of highlighted items
 * - Provides comprehensive accessibility support (ARIA attributes, screen readers)
 * - Shows loading skeletons and error states
 *
 * **Performance Optimizations:**
 * - Uses useRef to avoid re-creating event listeners on every render
 * - Memoizes callbacks to prevent unnecessary child re-renders
 *
 * **Keyboard Navigation:**
 * - Arrow keys navigate through suggestions
 * - Home/End jump to first/last item
 * - Enter selects the highlighted suggestion
 * - Escape closes the dropdown
 * - Tab key allows normal page navigation (accessibility requirement)
 *
 * **Accessibility:**
 * - Full WCAG 2.1 AA compliance
 * - Screen reader announcements for result counts
 * - Proper ARIA roles and attributes
 * - Visual focus indicators
 *
 * @module components/DSO/DSOSearchField/DSOSuggestionList
 * @internal
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { filterProducts, handleOnClick, label } from './DSOSearchField.helper';
import reactStringReplace from 'react-string-replace';
import { Styled } from '.';
import { useRouter } from 'next/router';
import { useDsoProductSearch } from '../../../reactQuery/queries/dso';
import { useI18n } from '../../../contexts/i18n';

interface DSOSuggestionListProps {
  searchText: string;
  hideSearchResultsModal(): void;
  maxResults?: number;
  showAll?: boolean;
}

/**
 * Skeleton loader component for suggestion list
 *
 * @component
 * @param {Object} props
 * @param {number} [props.count=3] - Number of skeleton items to render
 * @returns {JSX.Element} Skeleton loading UI with shimmer animation
 */
const SkeletonLoader: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <Styled.LoadingContainer data-testid="dso-loading-indicator">
      {Array.from({ length: count }).map((_, index) => (
        <Styled.SkeletonItem key={index}>
          <Styled.SkeletonLine width="70%" height="16px" />
          <Styled.SkeletonLine width="40%" height="12px" />
        </Styled.SkeletonItem>
      ))}
    </Styled.LoadingContainer>
  );
};

/**
 * DSOSuggestionList component - displays filtered product suggestions
 *
 * @component
 * @internal
 *
 * @description
 * Renders a dropdown list of product suggestions with advanced keyboard navigation,
 * screen reader support, and skeleton loading states. This component manages the
 * keyboard interaction state and accessibility attributes for the suggestion dropdown.
 *
 * @keyboard
 * **Keyboard Navigation (handled by this component):**
 * - `Arrow Down` - Highlights next suggestion in list, scrolls into view
 * - `Arrow Up` - Highlights previous suggestion, moves to top when at first item
 * - `Home` - Jumps to first suggestion
 * - `End` - Jumps to last suggestion
 * - `Enter` - Navigates to highlighted product detail page
 * - `Tab` - Navigates to highlighted product detail page
 * - `Mouse Enter` - Highlights suggestion on hover
 *
 * @features
 * - **Auto-scroll**: Highlighted items automatically scroll into view
 * - **Visual Feedback**: Highlighted items show blue background and left border
 * - **State Management**: Tracks highlighted index with useState
 * - **Accessibility**: Updates aria-activedescendant and aria-selected
 * - **Screen Reader**: Announces result count and navigation instructions
 *
 * @accessibility
 * - `role="listbox"` - Identifies as a listbox widget
 * - `aria-label` - Provides accessible name
 * - `aria-activedescendant` - References currently highlighted option
 * - `aria-busy` - Indicates loading state
 * - `aria-live` regions for dynamic announcements
 * - Individual items have `role="option"` and `aria-selected`
 *
 * @param {DSOSuggestionListProps} props - Component props
 * @param {string} props.searchText - Current search query text
 * @param {Function} props.hideSearchResultsModal - Callback to close the dropdown
 * @param {number} [props.maxResults=8] - Maximum suggestions to display
 *
 * @see {@link DSOSearchField} parent component
 * @see {@link SkeletonLoader} loading state component
 */
function DSOSuggestionList({
  searchText,
  hideSearchResultsModal,
  maxResults = 8,
  showAll = true,
}: DSOSuggestionListProps) {
  const router = useRouter();
  const { data, isLoading, isError, error } = useDsoProductSearch();
  const i18n = useI18n();
  const listRef = useRef<HTMLDivElement>(null);

  // State for keyboard navigation
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  // Ref array for direct element access (more efficient than querySelector)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Get the matching search results with filtering
  const filteredResults = filterProducts(
    searchText,
    data?.products,
    maxResults,
    showAll
  );

  // Check if there are filtered results
  const isMatchingSearchResult = filteredResults?.length > 0;

  // Store latest values in ref to avoid recreating event handler
  // This prevents the event listener from being removed/re-added on every render
  const handlersRef = useRef({
    filteredResults,
    hideSearchResultsModal,
    router,
    isMatchingSearchResult,
    highlightedIndex,
  });

  // Update refs with latest values
  useEffect(() => {
    handlersRef.current = {
      filteredResults,
      hideSearchResultsModal,
      router,
      isMatchingSearchResult,
      highlightedIndex,
    };
  });

  // Reset highlighted index when search text changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchText]);

  // Handle keyboard navigation
  // Uses ref pattern to avoid recreating the handler on every render
  // This prevents event listener thrashing (remove/re-add on each render)
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const {
      filteredResults,
      hideSearchResultsModal,
      router,
      isMatchingSearchResult,
      highlightedIndex,
    } = handlersRef.current;

    // Handle Escape key to close dropdown
    if (event.key === 'Escape') {
      event.preventDefault();
      hideSearchResultsModal();
      return;
    }

    if (!isMatchingSearchResult) return;

    const totalItems = filteredResults.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev => (prev < totalItems - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Home':
        event.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setHighlightedIndex(totalItems - 1);
        break;
      case 'Enter':
        // Only handle Enter if an item is highlighted
        if (highlightedIndex >= 0 && highlightedIndex < totalItems) {
          event.preventDefault();
          const selectedProduct = filteredResults[highlightedIndex];
          handleOnClick(selectedProduct.slug, hideSearchResultsModal, router);
        }
        break;
      // Note: Tab key is intentionally NOT intercepted to allow normal page navigation
      // This is required for WCAG 2.1 AA accessibility compliance
    }
  }, []); // Empty deps - handler never recreates, uses ref values

  // Attach keyboard event listener once
  // Since handleKeyDown has empty deps, this effect only runs on mount/unmount
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Scroll highlighted item into view using direct ref access
  useEffect(() => {
    const element = itemRefs.current[highlightedIndex];
    if (element) {
      element.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  // When status isLoading, display skeleton loader
  if (isLoading) {
    return (
      <>
        <Styled.ScreenReaderOnly
          aria-live="polite"
          data-testid="dso-sr-loading"
        >
          Loading product suggestions...
        </Styled.ScreenReaderOnly>
        <Styled.SuggestionList
          data-testid="dso-suggestions-dropdown"
          role="listbox"
          aria-label="Loading product suggestions"
          aria-busy="true"
        >
          <SkeletonLoader count={maxResults > 5 ? 5 : maxResults} />
        </Styled.SuggestionList>
      </>
    );
  }

  // When status isError, display error message
  if (isError) {
    console.error('DSO Product Search Error:', error);
    return (
      <>
        <Styled.ErrorMessage
          data-testid="dso-error-message"
          role="alert"
          aria-live="assertive"
        >
          {label(i18n).errorOccured}
        </Styled.ErrorMessage>
        <Styled.ScreenReaderOnly aria-live="polite">
          Error occurred while fetching product suggestions. Please try again
          later.
        </Styled.ScreenReaderOnly>
      </>
    );
  }

  // Generate screen reader announcement for results count
  const resultsAnnouncement = isMatchingSearchResult
    ? `${filteredResults.length} product${
        filteredResults.length === 1 ? '' : 's'
      } found. Use arrow keys to navigate, Enter to select, Escape to close.`
    : 'No products found matching your search.';

  return (
    <>
      <Styled.ScreenReaderOnly
        aria-live="polite"
        aria-atomic="true"
        data-testid="dso-sr-announcement"
      >
        {resultsAnnouncement}
      </Styled.ScreenReaderOnly>
      <Styled.SuggestionList
        ref={listRef}
        data-testid="dso-suggestions-dropdown"
        role="listbox"
        aria-label="Product suggestions"
        aria-activedescendant={
          highlightedIndex >= 0
            ? `dso-suggestion-item-${filteredResults[highlightedIndex]?.sku}`
            : undefined
        }
      >
        {isMatchingSearchResult ? (
          filteredResults.map((filteredResult, index) => {
            const isHighlighted = index === highlightedIndex;
            return (
              <Styled.SuggestionListItem
                key={filteredResult.sku}
                id={`dso-suggestion-item-${filteredResult.sku}`}
                ref={(el) => { itemRefs.current[index] = el; }}
                data-index={index}
                data-testid={`dso-suggestion-item-${filteredResult.sku}`}
                role="option"
                aria-selected={isHighlighted}
                tabIndex={-1}
                style={{
                  background: isHighlighted
                    ? 'rgba(32, 154, 214, 0.12)'
                    : undefined,
                }}
                onClick={() =>
                  handleOnClick(
                    filteredResult.slug,
                    hideSearchResultsModal,
                    router
                  )
                }
                onMouseEnter={() => setHighlightedIndex(index)}
                onKeyPress={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOnClick(
                      filteredResult.slug,
                      hideSearchResultsModal,
                      router
                    );
                  }
                }}
              >
                {/* Display product name with highlighted search text */}
                <Styled.ItemName>
                  {reactStringReplace(
                    filteredResult.name,
                    searchText,
                    (match: string, index) => (
                      <strong key={index}>{match}</strong>
                    )
                  )}
                </Styled.ItemName>

                {/* Display SKU with highlighted search text */}
                <Styled.ItemSKU>
                  SKU:{' '}
                  {reactStringReplace(
                    filteredResult.sku,
                    searchText,
                    (match: string, index) => (
                      <strong key={index}>{match}</strong>
                    )
                  )}
                </Styled.ItemSKU>
              </Styled.SuggestionListItem>
            );
          })
        ) : (
          // Display "no results" message when no matches found
          <Styled.Label data-testid="dso-no-results">
            {label(i18n).noresults}
          </Styled.Label>
        )}
      </Styled.SuggestionList>
    </>
  );
}

export default DSOSuggestionList;
